import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { PageTitle } from '../../../../../_metronic/layout/core'
import Swal from 'sweetalert2'
import { ReportShell } from '../components/ReportShell'
import { loadSellerTicketAverageReport } from '../services'
import {
  SellerReportSourceFilter,
  SellerTicketAverageFilters,
  SellerTicketAverageReport,
} from '../types'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

type GroupByMode = 'day' | 'week' | 'month'

interface TicketTrendRow {
  key: string
  label: string
  orderCount: number
  totalAmountCents: number
  averageTicketCents: number
  previousAverageTicketCents: number | null
  variationPercent: number | null
}

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
})

const percentFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})

const today = new Date()
const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)

const toDateInputValue = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

const formatCurrency = (amountCents: number): string => currencyFormatter.format(amountCents / 100)

const parseOrderDate = (rawDate?: string | null): Date | null => {
  if (!rawDate) {
    return null
  }

  const parsed = new Date(rawDate)
  if (Number.isNaN(parsed.getTime())) {
    return null
  }

  return parsed
}

const getWeekInfo = (date: Date): { year: number; week: number } => {
  const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
  const dayNumber = utcDate.getUTCDay() || 7
  utcDate.setUTCDate(utcDate.getUTCDate() + 4 - dayNumber)
  const yearStart = new Date(Date.UTC(utcDate.getUTCFullYear(), 0, 1))
  const week = Math.ceil((((utcDate.getTime() - yearStart.getTime()) / 86400000) + 1) / 7)

  return {
    year: utcDate.getUTCFullYear(),
    week,
  }
}

const getPeriodFromDate = (date: Date, groupBy: GroupByMode): { key: string; label: string } => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  if (groupBy === 'day') {
    return {
      key: `${year}-${month}-${day}`,
      label: `${day}/${month}/${year}`,
    }
  }

  if (groupBy === 'month') {
    return {
      key: `${year}-${month}`,
      label: `${month}/${year}`,
    }
  }

  const weekInfo = getWeekInfo(date)
  const weekLabel = String(weekInfo.week).padStart(2, '0')

  return {
    key: `${weekInfo.year}-W${weekLabel}`,
    label: `Semana ${weekLabel}/${weekInfo.year}`,
  }
}

const buildTrendRows = (report: SellerTicketAverageReport | null, groupBy: GroupByMode): TicketTrendRow[] => {
  if (!report?.items.length) {
    return []
  }

  const grouped = new Map<string, { label: string; orderCount: number; totalAmountCents: number }>()

  for (const order of report.items) {
    const date = parseOrderDate(order.created)
    if (!date) {
      continue
    }

    const period = getPeriodFromDate(date, groupBy)
    const current = grouped.get(period.key) || {
      label: period.label,
      orderCount: 0,
      totalAmountCents: 0,
    }

    current.orderCount += 1
    current.totalAmountCents += typeof order.total === 'number' ? Math.round(order.total) : 0
    grouped.set(period.key, current)
  }

  const baseRows = Array.from(grouped.entries())
    .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
    .map(([key, value]) => ({
      key,
      label: value.label,
      orderCount: value.orderCount,
      totalAmountCents: value.totalAmountCents,
      averageTicketCents: value.orderCount > 0 ? Math.round(value.totalAmountCents / value.orderCount) : 0,
    }))

  return baseRows.map((current, index) => {
    const previous = index > 0 ? baseRows[index - 1] : null
    const previousAverageTicketCents = previous?.averageTicketCents ?? null
    const variationPercent =
      previousAverageTicketCents && previousAverageTicketCents > 0
        ? ((current.averageTicketCents - previousAverageTicketCents) / previousAverageTicketCents) * 100
        : null

    return {
      ...current,
      previousAverageTicketCents,
      variationPercent,
    }
  })
}

const trendColor = (variationPercent: number | null): string => {
  if (variationPercent === null) {
    return 'text-gray-600'
  }

  if (variationPercent > 0) {
    return 'text-success'
  }

  if (variationPercent < 0) {
    return 'text-danger'
  }

  return 'text-gray-700'
}

const trendLabel = (variationPercent: number | null): string => {
  if (variationPercent === null) {
    return '-'
  }

  const signal = variationPercent > 0 ? '+' : ''
  return `${signal}${percentFormatter.format(variationPercent)}%`
}

const exportToCSV = (filename: string, rows: TicketTrendRow[], groupBy: GroupByMode): void => {
  let csv = 'sep=;\n'
  csv += `Agrupamento atual;${groupBy}\n`
  csv += 'Periodo;Pedidos;Faturamento;Ticket medio;Ticket medio anterior;Variação (%)\n'

  rows.forEach((row) => {
    csv += `"${row.label}";${row.orderCount};${row.totalAmountCents / 100};${row.averageTicketCents / 100};${row.previousAverageTicketCents ? row.previousAverageTicketCents / 100 : ''};${row.variationPercent === null ? '' : row.variationPercent.toFixed(1)}\n`
  })

  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const exportToXLSX = (filename: string, rows: TicketTrendRow[], groupBy: GroupByMode): void => {
  const workbook = XLSX.utils.book_new()
  const sheet = XLSX.utils.json_to_sheet(
    rows.map((row) => ({
      Periodo: row.label,
      Pedidos: row.orderCount,
      Faturamento: row.totalAmountCents / 100,
      'Ticket medio': row.averageTicketCents / 100,
      'Ticket medio anterior': row.previousAverageTicketCents ? row.previousAverageTicketCents / 100 : null,
      'Variação (%)': row.variationPercent === null ? null : row.variationPercent.toFixed(1),
      Agrupamento: groupBy,
    }))
  )

  XLSX.utils.book_append_sheet(workbook, sheet, 'Tendencia')
  XLSX.writeFile(workbook, filename)
}

const exportToPDF = (
  filename: string,
  rows: TicketTrendRow[],
  groupBy: GroupByMode,
  report: SellerTicketAverageReport | null
): void => {
  const pdf = new jsPDF()
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  const margin = 15
  let yPosition = margin

  pdf.setFontSize(16)
  pdf.text('Relatório de Tendência de Ticket Médio', margin, yPosition)
  yPosition += 8

  pdf.setFontSize(10)
  pdf.setTextColor(100, 100, 100)
  pdf.text(`Relatório gerado em: ${new Date().toLocaleString('pt-BR')}`, margin, yPosition)
  yPosition += 12

  pdf.setFontSize(11)
  pdf.setTextColor(0, 0, 0)
  pdf.text(`Agrupamento: ${groupBy}`, margin, yPosition)
  yPosition += 6

  pdf.setFontSize(9)
  pdf.text(`Pedidos totais: ${report?.summary.totalOrders || 0} | Ticket médio geral: ${formatCurrency(report?.summary.averageTicketCents || 0)}`, margin, yPosition)
  yPosition += 10

  autoTable(pdf, {
    startY: yPosition,
    head: [['Período', 'Pedidos', 'Faturamento', 'Ticket médio', 'Ticket anterior', 'Variação (%)']],
    body: rows.map((row) => [
      row.label,
      row.orderCount,
      formatCurrency(row.totalAmountCents),
      formatCurrency(row.averageTicketCents),
      row.previousAverageTicketCents ? formatCurrency(row.previousAverageTicketCents) : '-',
      row.variationPercent === null ? '-' : row.variationPercent.toFixed(1),
    ]),
    margin: { left: margin, right: margin },
    headStyles: { fillColor: [41, 128, 185], textColor: 255, fontSize: 9, fontStyle: 'bold' },
    bodyStyles: { fontSize: 8, textColor: 50 },
    alternateRowStyles: { fillColor: [245, 245, 245] },
  })

  const pageCount = (pdf as any).internal.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i)
    pdf.setFontSize(8)
    pdf.setTextColor(150, 150, 150)
    pdf.text(`Página ${i} de ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' })
  }

  pdf.save(filename)
}

const TicketTrendReportPage: FC = () => {
  const [filters, setFilters] = useState<SellerTicketAverageFilters>({
    startDate: toDateInputValue(firstDayOfMonth),
    finalDate: toDateInputValue(today),
    source: 'all',
  })
  const [groupBy, setGroupBy] = useState<GroupByMode>('day')
  const [loading, setLoading] = useState(false)
  const [report, setReport] = useState<SellerTicketAverageReport | null>(null)

  const loadReport = useCallback(async () => {
    try {
      setLoading(true)
      const data = await loadSellerTicketAverageReport(filters)
      setReport(data)
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erro ao carregar relatório',
        text: error instanceof Error ? error.message : 'Erro desconhecido',
        confirmButtonText: 'OK',
      })
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    void loadReport()
  }, [loadReport])

  const sourceOptions = useMemo(
    () => [
      { value: 'all' as const, label: 'Todas as fontes' },
      { value: 'ninegrid' as const, label: 'Ninegrid' },
      { value: 'totvs' as const, label: 'TOTVS' },
    ],
    []
  )

  const trendRows = useMemo(() => buildTrendRows(report, groupBy), [report, groupBy])

  const bestGrowth = useMemo(() => {
    const candidates = trendRows.filter((row) => row.variationPercent !== null)
    if (!candidates.length) {
      return null
    }

    return [...candidates].sort(
      (left, right) => (right.variationPercent || 0) - (left.variationPercent || 0)
    )[0]
  }, [trendRows])

  const worstGrowth = useMemo(() => {
    const candidates = trendRows.filter((row) => row.variationPercent !== null)
    if (!candidates.length) {
      return null
    }

    return [...candidates].sort(
      (left, right) => (left.variationPercent || 0) - (right.variationPercent || 0)
    )[0]
  }, [trendRows])

  const onSourceChange = (source: SellerReportSourceFilter) => {
    setFilters((previous) => ({
      ...previous,
      source,
    }))
  }

  return (
    <>
      <PageTitle breadcrumbs={[{ title: 'Tendência de Ticket Médio', path: '/seller/reports/ticket-trend', isActive: true }]}>
        Relatório de Tendência de Ticket Médio
      </PageTitle>

      <ReportShell
        title='Tendência de Ticket Médio por Período'
        description='Evolução temporal do ticket médio com variação percentual em relação ao período anterior.'
        actions={
          <div className='d-flex gap-2'>
            <button className='btn btn-primary' onClick={loadReport} disabled={loading}>
              <i className='fas fa-sync-alt me-2'></i>
              {loading ? 'Carregando...' : 'Atualizar'}
            </button>
            <button className='btn btn-light-success' onClick={() => {
              const timestamp = new Date().toISOString().split('T')[0]
              exportToCSV(`Tendencia-Ticket-${timestamp}.csv`, trendRows, groupBy)
            }} disabled={loading || trendRows.length === 0} title='Baixar relatório em CSV'>
              <i className='fas fa-file-csv me-2'></i> CSV
            </button>
            <button className='btn btn-light-info' onClick={() => {
              const timestamp = new Date().toISOString().split('T')[0]
              exportToXLSX(`Tendencia-Ticket-${timestamp}.xlsx`, trendRows, groupBy)
            }} disabled={loading || trendRows.length === 0} title='Baixar relatório em XLSX'>
              <i className='fas fa-file-excel me-2'></i> XLSX
            </button>
            <button className='btn btn-light-danger' onClick={() => {
              const timestamp = new Date().toISOString().split('T')[0]
              exportToPDF(`Tendencia-Ticket-${timestamp}.pdf`, trendRows, groupBy, report)
            }} disabled={loading || trendRows.length === 0} title='Baixar relatório em PDF'>
              <i className='fas fa-file-pdf me-2'></i> PDF
            </button>
          </div>
        }
        filters={
          <div className='row g-3 align-items-end'>
            <div className='col-12 col-md-3'>
              <label className='form-label fw-semibold'>Data inicial</label>
              <input
                className='form-control'
                type='date'
                value={filters.startDate}
                onChange={(event) =>
                  setFilters((previous) => ({ ...previous, startDate: event.target.value }))
                }
              />
            </div>
            <div className='col-12 col-md-3'>
              <label className='form-label fw-semibold'>Data final</label>
              <input
                className='form-control'
                type='date'
                value={filters.finalDate}
                onChange={(event) =>
                  setFilters((previous) => ({ ...previous, finalDate: event.target.value }))
                }
              />
            </div>
            <div className='col-12 col-md-2'>
              <label className='form-label fw-semibold'>Fonte</label>
              <select
                className='form-select'
                value={filters.source}
                onChange={(event) => onSourceChange(event.target.value as SellerReportSourceFilter)}
              >
                {sourceOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className='col-12 col-md-2'>
              <label className='form-label fw-semibold'>Agrupar por</label>
              <select
                className='form-select'
                value={groupBy}
                onChange={(event) => setGroupBy(event.target.value as GroupByMode)}
              >
                <option value='day'>Dia</option>
                <option value='week'>Semana</option>
                <option value='month'>Mês</option>
              </select>
            </div>
            <div className='col-12 col-md-2 d-grid'>
              <button className='btn btn-light-primary' onClick={loadReport} disabled={loading}>
                Buscar
              </button>
            </div>
          </div>
        }
      >
        <div className='row g-4 mb-4'>
          <div className='col-12 col-md-4'>
            <div className='card border-0 shadow-sm h-100'>
              <div className='card-body'>
                <div className='text-gray-600 fw-semibold mb-1'>Ticket médio geral</div>
                <div className='fs-2hx fw-bold text-primary'>
                  {formatCurrency(report?.summary.averageTicketCents || 0)}
                </div>
              </div>
            </div>
          </div>
          <div className='col-12 col-md-4'>
            <div className='card border-0 shadow-sm h-100'>
              <div className='card-body'>
                <div className='text-gray-600 fw-semibold mb-1'>Maior alta</div>
                <div className='fs-3 fw-bold text-success'>{bestGrowth ? bestGrowth.label : 'Sem dados'}</div>
                <div className='mt-2 fw-semibold text-success'>
                  {bestGrowth ? trendLabel(bestGrowth.variationPercent) : '-'}
                </div>
              </div>
            </div>
          </div>
          <div className='col-12 col-md-4'>
            <div className='card border-0 shadow-sm h-100'>
              <div className='card-body'>
                <div className='text-gray-600 fw-semibold mb-1'>Maior queda</div>
                <div className='fs-3 fw-bold text-danger'>{worstGrowth ? worstGrowth.label : 'Sem dados'}</div>
                <div className='mt-2 fw-semibold text-danger'>
                  {worstGrowth ? trendLabel(worstGrowth.variationPercent) : '-'}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='card border-0 shadow-sm'>
          <div className='card-header border-0 pt-5'>
            <div className='card-title d-flex flex-column'>
              <span className='fs-3 fw-bold text-dark'>Variação de ticket por período</span>
              <span className='text-gray-600 mt-1'>Comparativo com o período anterior para identificar tendência.</span>
            </div>
          </div>
          <div className='card-body pt-0'>
            {loading ? (
              <div className='d-flex justify-content-center align-items-center py-10'>
                <div className='spinner-border text-primary' role='status'>
                  <span className='visually-hidden'>Carregando...</span>
                </div>
              </div>
            ) : trendRows.length > 0 ? (
              <div className='table-responsive'>
                <table className='table align-middle table-row-dashed fs-6 gy-3'>
                  <thead>
                    <tr className='text-start text-gray-600 fw-bold fs-7 text-uppercase gs-0'>
                      <th>Período</th>
                      <th className='text-end'>Pedidos</th>
                      <th className='text-end'>Faturamento</th>
                      <th className='text-end'>Ticket médio</th>
                      <th className='text-end'>Período anterior</th>
                      <th className='text-end'>Variação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trendRows.map((row) => (
                      <tr key={row.key}>
                        <td>
                          <div className='fw-bold text-dark'>{row.label}</div>
                        </td>
                        <td className='text-end fw-bold'>{row.orderCount}</td>
                        <td className='text-end'>{formatCurrency(row.totalAmountCents)}</td>
                        <td className='text-end fw-bold text-primary'>
                          {formatCurrency(row.averageTicketCents)}
                        </td>
                        <td className='text-end'>
                          {row.previousAverageTicketCents !== null
                            ? formatCurrency(row.previousAverageTicketCents)
                            : '-'}
                        </td>
                        <td className={`text-end fw-bold ${trendColor(row.variationPercent)}`}>
                          {trendLabel(row.variationPercent)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className='alert alert-light-info mb-0'>
                Nenhum pedido encontrado para os filtros selecionados.
              </div>
            )}
          </div>
        </div>
      </ReportShell>
    </>
  )
}

export { TicketTrendReportPage }